import { apiClient, getValidAccessToken } from '@shared/api/client';
import { apiRoutes } from '@shared/api/routes';
import { getRefreshToken, getSessionGeneration, setTokens } from '@shared/api/sessionTokens';
import { permissions as knownPermissions } from '@shared/auth/permissions';
import type { Permission, RoleKey } from '@shared/auth/permissions';
import type { CurrentUser } from '@features/auth/store/authStore';

export interface LoginRequest {
  username: string;
  password: string;
}

export type AgentAvailability = 'available' | 'busy' | 'away';

export interface UserAvailabilityResponse {
  availability: AgentAvailability;
  updatedAtUtc: string;
  syncWarnings?: Array<{ channel: 'livechat' | 'whatsapp'; code: string }> | null;
}

export interface LoginChallenge {
  requiresOtp: boolean;
  challengeId: string;
  maskedDestination: string;
  expiresAtUtc: string;
}

export interface OtpChallenge {
  challengeId: string;
  maskedDestination: string;
  expiresAtUtc: string;
}

export interface LoginResponse {
  isSuccess: boolean;
  data: LoginChallenge | null;
  message: string;
}

export async function login(request: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>(apiRoutes.auth.login, request);
  return response.data;
}

export interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAtUtc: string;
  refreshTokenExpiresAtUtc: string;
  user: {
    id: string;
    externalAdministratorId: number;
    username: string;
    displayName: string;
    email: string | null;
    departmentId: number | null;
    departmentName: string | null;
    role: RoleKey;
    language: string;
    permissions: Permission[];
  };
}

interface AuthActionResponse<T> {
  status: number;
  isSuccess: boolean;
  data: T | null;
  message: string;
  errorCode?: string;
}

export async function verifyOtp(
  challengeId: string,
  code: string,
): Promise<AuthActionResponse<SessionResponse>> {
  const response = await apiClient.post<AuthActionResponse<SessionResponse>>(
    apiRoutes.auth.verifyOtp,
    {
      challengeId,
      code,
    },
  );
  if (response.data.isSuccess && response.data.data !== null) setTokens(response.data.data);
  return response.data;
}

export async function resendOtp(challengeId: string): Promise<AuthActionResponse<LoginChallenge>> {
  return (
    await apiClient.post<AuthActionResponse<LoginChallenge>>(apiRoutes.auth.resendOtp, {
      challengeId,
    })
  ).data;
}

export async function forgotPassword(username: string): Promise<AuthActionResponse<OtpChallenge>> {
  return (
    await apiClient.post<AuthActionResponse<OtpChallenge>>(apiRoutes.auth.forgotPassword, {
      username,
    })
  ).data;
}

export async function resetPassword(
  challengeId: string,
  code: string,
  newPassword: string,
): Promise<AuthActionResponse<SessionResponse>> {
  const response = await apiClient.post<AuthActionResponse<SessionResponse>>(
    apiRoutes.auth.resetPassword,
    {
      challengeId,
      code,
      newPassword,
    },
  );
  if (response.data.isSuccess && response.data.data !== null) setTokens(response.data.data);
  return response.data;
}

export async function logoutSession(): Promise<void> {
  const generation = getSessionGeneration();
  // Renew before capturing the body; otherwise it can contain the pre-rotation token.
  await getValidAccessToken();
  if (getSessionGeneration() !== generation) return;
  const refreshToken = getRefreshToken();
  if (refreshToken === null) return;
  await apiClient.post(
    apiRoutes.auth.logout,
    { refreshToken },
    { _helixSessionGeneration: generation },
  );
}

interface CurrentUserApiResponse {
  id: string;
  displayName: string;
  email: string | null;
  departmentId: number | null;
  departmentName: string | null;
  role: string | { id: string; name: string };
  language?: string | null;
  permissions: string[];
}

const roles: RoleKey[] = [
  'agent',
  'senior',
  'supervisor',
  'knowledgeManager',
  'administrator',
  'readOnlyQa',
];

function normalizeRole(value: CurrentUserApiResponse['role']): RoleKey {
  const raw = typeof value === 'string' ? value : value.id;
  const normalized = raw.replaceAll(/[-_\s]/g, '').toLowerCase();
  return roles.find((role) => role.toLowerCase() === normalized) ?? 'agent';
}

export async function getCurrentUser(signal?: AbortSignal): Promise<CurrentUser> {
  const response = await apiClient.get<CurrentUserApiResponse>(
    apiRoutes.auth.me,
    signal ? { signal } : {},
  );
  const user = response.data;
  const granted = user.permissions.filter((value): value is Permission =>
    (knownPermissions as readonly string[]).includes(value),
  );
  return {
    id: user.id,
    name: user.displayName,
    email: user.email ?? '',
    role: normalizeRole(user.role),
    language: user.language ?? 'en',
    departmentId: user.departmentId,
    departmentName: user.departmentName,
    permissions: granted,
  };
}

export async function getUserAvailability(): Promise<UserAvailabilityResponse> {
  const response = await apiClient.get<UserAvailabilityResponse>(apiRoutes.auth.availability);
  return response.data;
}

export async function updateUserAvailability(
  availability: AgentAvailability,
): Promise<UserAvailabilityResponse> {
  const response = await apiClient.put<UserAvailabilityResponse>(apiRoutes.auth.availability, {
    availability,
  });
  return response.data;
}
